import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

import { Button, IconButton, PrimaryButton } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  InlineActionButton,
  DrawerTitle,
  OptionsDrawer,
} from 'containers/AuditFormBuilder/components/OptionDrawer/styles';

import { LibraryEntityType } from 'utils/api/modules/entities/entities.types';

import { getEntityTranslation } from 'stores/EntityDetails/utils';

import CategoryRow from './CategoryRow';
import { CategoryRows, DrawerIntro } from './styles';
import useCategoryDrafts from './useCategoryDrafts';

type LibraryCategoriesDrawerProps = {
  entityType: LibraryEntityType;
  onClose: (changed: boolean) => void;
};

const LibraryCategoriesDrawer: React.FunctionComponent<LibraryCategoriesDrawerProps> = ({ entityType, onClose }) => {
  const { singular, plural } = getEntityTranslation[entityType];
  const noun = plural.toLowerCase();
  const {
    drafts,
    visible,
    loadFailed,
    rowErrors,
    blockedId,
    focusId,
    saving,
    isDirty,
    hasEmptyExisting,
    load,
    close,
    onAddRow,
    onChange,
    onDelete,
    onSave,
  } = useCategoryDrafts(entityType, onClose);

  const body = (): React.ReactNode => {
    if (loadFailed) {
      return (
        <>
          <DrawerIntro>The categories could not be loaded.</DrawerIntro>
          <PrimaryButton onClick={() => load()}>Retry</PrimaryButton>
        </>
      );
    }
    if (!drafts) {
      return <CenteredCircularProgress data-testid="categories-loading" />;
    }
    return (
      <>
        <CategoryRows>
          {visible.map((draft) => (
            <CategoryRow
              key={draft.id}
              draft={draft}
              noun={noun}
              error={rowErrors[draft.id]}
              showUsedBy={blockedId === draft.id}
              autoFocus={focusId === draft.id}
              onChange={onChange}
              onDelete={onDelete}
            />
          ))}
        </CategoryRows>
        <InlineActionButton variant="text" startIcon={<AddIcon />} onClick={onAddRow} data-testid="category-add-button">
          Add category
        </InlineActionButton>
      </>
    );
  };

  return (
    <OptionsDrawer anchor="right" open onClose={close} data-testid="categories-drawer">
      <DrawerHeader>
        <DrawerTitle>{`${singular} categories`}</DrawerTitle>
        <IconButton aria-label="Close" onClick={close}>
          <CloseIcon />
        </IconButton>
      </DrawerHeader>
      <DrawerBody>{body()}</DrawerBody>
      <DrawerFooter>
        <Button variant="outlined" size="large" onClick={close} data-testid="category-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={!drafts || !isDirty || hasEmptyExisting || saving}
          onClick={onSave}
          data-testid="category-save"
        >
          Save
        </Button>
      </DrawerFooter>
    </OptionsDrawer>
  );
};

export default LibraryCategoriesDrawer;
